import { useState, useRef, useMemo } from 'react';
import {
  FileText,
  FilePlus,
  Upload,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Folder,
  File,
  FileCode,
  FileJson,
  FileType,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import type { AgentSkill } from '../../../../api/agent-skills';
import { useUploadSkillFile } from '../../../../api/agent-skills';
import { getApiErrorMessage } from '../../../../lib/utils';
import { useSkillFiles } from '../hooks/useSkillFiles';
import { buildFileTree } from '../utils/file-tree';
import { formatBytes } from '../utils/constants';
import { ConfirmDialog } from './ConfirmDialog';
import {
  CodePane,
  langFromPath,
} from '../../../../components/Pleri/components/MessageContent/codeBlockUtils';
import type { FileTreeNode } from '../types';

import { useTranslation } from 'react-i18next';

/** Return a file-type icon based on extension */
function fileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (
    ['ts', 'tsx', 'js', 'jsx', 'py', 'go', 'rs', 'java', 'sh', 'bash'].includes(
      ext,
    )
  ) {
    return <FileCode className="h-3.5 w-3.5 text-blue-400" />;
  }
  if (ext === 'json') {
    return <FileJson className="h-3.5 w-3.5 text-orange-400" />;
  }
  if (['md', 'mdx', 'txt', 'rst'].includes(ext)) {
    return <FileText className="h-3.5 w-3.5 text-gray-400" />;
  }
  if (['yaml', 'yml', 'toml', 'ini', 'env', 'cfg'].includes(ext)) {
    return <FileType className="h-3.5 w-3.5 text-purple-400" />;
  }
  return <File className="h-3.5 w-3.5 text-gray-400" />;
}

/** Check whether a file path likely contains text content */
function isTextFile(path: string): boolean {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  const textExts = [
    'ts',
    'tsx',
    'js',
    'jsx',
    'py',
    'go',
    'rs',
    'java',
    'sh',
    'bash',
    'json',
    'yaml',
    'yml',
    'toml',
    'ini',
    'env',
    'cfg',
    'md',
    'mdx',
    'txt',
    'rst',
    'html',
    'htm',
    'css',
    'scss',
    'xml',
    'sql',
    'graphql',
    'tf',
    'tfvars',
    'hcl',
    'rb',
    'php',
    'swift',
    'kt',
    'cs',
    'c',
    'cpp',
    'h',
    'csv',
    'log',
  ];
  return textExts.includes(ext);
}

interface SkillFileManagerProps {
  skill: AgentSkill;
  readonly?: boolean;
}

// ---------------------------------------------------------------------------
// File tree view (recursive)
// ---------------------------------------------------------------------------
function FileTreeView({
  nodes,
  depth = 0,
  onSelectFile,
  selectedPath,
  onDeleteFile,
}: {
  nodes: FileTreeNode[];
  depth?: number;
  onSelectFile: (path: string) => void;
  selectedPath?: string | null;
  onDeleteFile?: (path: string) => void;
}) {
  const { t } = useTranslation('pleri/skills');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return (
    <>
      {nodes.map((node) => {
        const isOpen = expanded[node.path] ?? true;
        const isSelected = !node.isDir && node.path === selectedPath;
        return (
          <div key={node.path}>
            <div
              className={`group flex cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors ${
                isSelected
                  ? 'bg-orange-50 text-orange-800 dark:bg-orange-950 dark:text-orange-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={{ paddingLeft: `${depth * 16 + 8}px` }}
              onClick={() => {
                if (node.isDir) {
                  setExpanded((prev) => ({ ...prev, [node.path]: !isOpen }));
                } else {
                  onSelectFile(node.path);
                }
              }}
            >
              {node.isDir ? (
                <>
                  {isOpen ? (
                    <ChevronDown className="h-3 w-3 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-3 w-3 text-gray-400" />
                  )}
                  {isOpen ? (
                    <FolderOpen className="h-3.5 w-3.5 text-orange-400" />
                  ) : (
                    <Folder className="h-3.5 w-3.5 text-orange-400" />
                  )}
                </>
              ) : (
                <>
                  <span className="w-3" />
                  {fileIcon(node.name)}
                </>
              )}
              <span className="text-gray-700 dark:text-gray-300">
                {node.name}
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                {node.file && (
                  <span className="text-xs text-gray-400">
                    {formatBytes(node.file.sizeBytes)}
                  </span>
                )}
                {!node.isDir && onDeleteFile && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteFile(node.path);
                    }}
                    className="hidden rounded p-0.5 text-gray-400 group-hover:block hover:text-red-500"
                    title={t('deleteFile')}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </span>
            </div>
            {node.isDir && isOpen && (
              <FileTreeView
                nodes={node.children}
                depth={depth + 1}
                onSelectFile={onSelectFile}
                selectedPath={selectedPath}
                onDeleteFile={onDeleteFile}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline add file form
// ---------------------------------------------------------------------------
function AddFileForm({
  skill,
  onClose,
}: {
  skill: AgentSkill;
  onClose: () => void;
}) {
  const { t } = useTranslation('pleri/skills');
  const uploadFile = useUploadSkillFile(skill);
  const [filePath, setFilePath] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [filePathError, setFilePathError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const text = await file.text();
    setContent(text);
    setContentError(null);
    if (!filePath) {
      setFilePath(file.name);
      setFilePathError(null);
    }
  };

  const handleSubmit = async () => {
    let valid = true;
    if (!filePath.trim()) {
      setFilePathError(t('filePathIsRequired'));
      valid = false;
    } else {
      setFilePathError(null);
    }
    if (!content.trim()) {
      setContentError(t('fileContentIsRequired'));
      valid = false;
    } else {
      setContentError(null);
    }
    if (!valid) return;

    setError(null);
    try {
      await uploadFile.mutateAsync({ path: filePath.trim(), content });
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to upload file'));
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-orange-200 bg-orange-50/50 p-3 dark:border-orange-800 dark:bg-orange-950/30">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
          {t('addAFile')}
        </span>
        <button
          onClick={onClose}
          className="text-gray-400 transition-colors duration-150 hover:text-gray-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="flex-shrink-0 text-red-400 hover:text-red-600"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div>
        <label className="mb-1 block text-xs text-gray-600 dark:text-gray-400">
          {t('filePath')} <span className="text-red-500">*</span>
        </label>
        <input
          className={`w-full rounded-md border px-2 py-1.5 font-mono text-xs dark:bg-gray-700 dark:text-gray-100 ${filePathError ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          placeholder={t('referencesPlaybookMd')}
          value={filePath}
          onChange={(e) => { setFilePath(e.target.value); setFilePathError(null); }}
        />
        {filePathError && (
          <p className="mt-0.5 text-xs text-red-500">{filePathError}</p>
        )}
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs text-gray-600 dark:text-gray-400">
            {t('content')} <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-xs text-orange-600 transition-colors duration-150 hover:text-orange-700"
          >
            {t('loadFromFile')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
        <textarea
          className={`w-full rounded-md border px-2 py-1.5 font-mono text-xs dark:bg-gray-700 dark:text-gray-100 ${contentError ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
          rows={6}
          value={content}
          onChange={(e) => { setContent(e.target.value); setContentError(null); }}
          placeholder={t('pasteOrLoadFileContent')}
        />
        {contentError && (
          <p className="mt-0.5 text-xs text-red-500">{contentError}</p>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded px-3 py-1.5 text-xs text-gray-600 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          {t('common:cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={uploadFile.isPending}
          className="rounded bg-orange-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploadFile.isPending ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('uploading')}
            </span>
          ) : (
            t('addFile')
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main SkillFileManager
// ---------------------------------------------------------------------------
export function SkillFileManager({ skill, readonly }: SkillFileManagerProps) {
  const { t } = useTranslation('pleri/skills');
  const fileManager = useSkillFiles(
    skill ? { scope: skill.scope, name: skill.name } : null,
  );
  const {
    isDragOver,
    onDragOver,
    onDragLeave,
    onDrop,
    dropError,
    clearDropError,
    uploadProgress,
    previewFile,
    loadFileContent,
    closePreview,
    isEditing,
    editingContent,
    setEditingContent,
    startEditing,
    cancelEditing,
    saveEdit,
    saveError,
    isSaving,
    showAddFile,
    setShowAddFile,
    confirmDeleteFile,
    setConfirmDeleteFile,
    handleDeleteFile,
    deleteFileError,
    clearDeleteFileError,
  } = fileManager;

  const fileTree = useMemo(
    () =>
      skill.files && skill.files.length > 0 ? buildFileTree(skill.files) : [],
    [skill.files],
  );

  const fileCount = skill.files?.length ?? 0;

  return (
    <div className="px-6 py-4">
      {/* Confirm delete file dialog */}
      <ConfirmDialog
        isOpen={!!confirmDeleteFile}
        title={t('deleteFile')}
        message={t('areYouSureYouWantToDeleteFile', { confirmDeleteFile })}
        confirmLabel={t('deleteFile')}
        error={deleteFileError}
        onConfirm={() => {
          if (confirmDeleteFile) {
            handleDeleteFile(confirmDeleteFile);
          }
        }}
        onCancel={() => { setConfirmDeleteFile(null); clearDeleteFileError(); }}
      />
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          <FileText className="h-3 w-3" />
          {t('filesHeading')}
          {fileCount > 0 && (
            <span className="ml-1 text-xs text-gray-400">
              ({fileCount})
            </span>
          )}
        </label>
        {!readonly && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddFile(true)}
              className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
            >
              <FilePlus className="h-3 w-3" />
              {t('addFileTitle')}
            </button>
          </div>
        )}
      </div>
      {/* Drop/upload error */}
      {dropError && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">{dropError}</span>
          <button onClick={clearDropError} className="flex-shrink-0 text-red-400 hover:text-red-600">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      {/* Add file inline form */}
      {showAddFile && !readonly && (
        <div className="mb-3">
          <AddFileForm skill={skill} onClose={() => setShowAddFile(false)} />
        </div>
      )}
      {/* Drop zone container */}
      <div
        className={`relative rounded-md border transition-all duration-200 ${
          isDragOver
            ? 'border-2 border-dashed border-orange-400 bg-orange-50/50 dark:border-orange-500 dark:bg-orange-950/20'
            : fileTree.length > 0
              ? 'border-gray-200 dark:border-gray-700'
              : 'border-dashed border-gray-300 dark:border-gray-600'
        }`}
        onDragOver={!readonly ? onDragOver : undefined}
        onDragLeave={!readonly ? onDragLeave : undefined}
        onDrop={!readonly ? onDrop : undefined}
      >
        {/* Drag overlay */}
        {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-orange-50/90 dark:bg-orange-950/80">
            <div className="text-center">
              <Upload className="mx-auto mb-2 h-8 w-8 animate-pulse text-orange-500" />
              <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                {t('dropFilesHere')}
              </p>
              <p className="mt-0.5 text-xs text-orange-500/70 dark:text-orange-400/60">
                {t('textFilesUpTo50Kb')}
              </p>
            </div>
          </div>
        )}

        {fileTree.length > 0 ? (
          <div className="flex min-h-[120px] flex-col md:flex-row">
            {/* Left: File tree */}
            <div
              className={`${previewFile ? 'border-b border-gray-200 md:w-1/3 md:border-r md:border-b-0 dark:border-gray-700' : 'w-full'} overflow-y-auto bg-gray-50/50 py-1 dark:bg-gray-900/50`}
            >
              <FileTreeView
                nodes={fileTree}
                onSelectFile={loadFileContent}
                selectedPath={previewFile?.path}
                onDeleteFile={
                  !readonly ? (path) => setConfirmDeleteFile(path) : undefined
                }
              />
            </div>

            {/* Right: Preview / edit panel */}
            {previewFile && (
              <div className="flex flex-1 flex-col overflow-hidden">
                {/* File header with metadata */}
                <div className="flex flex-col gap-1 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {fileIcon(previewFile.path)}
                      <code className="truncate text-xs font-medium text-gray-700 dark:text-gray-300">
                        {previewFile.path}
                      </code>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {!readonly && !isEditing && (
                        <button
                          onClick={startEditing}
                          className="rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-orange-500 dark:hover:bg-gray-700"
                          title={t('common:edit')}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                      {!readonly && !isEditing && (
                        <button
                          onClick={() => setConfirmDeleteFile(previewFile.path)}
                          className="rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30"
                          title={t('deleteFile')}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                      {isEditing && (
                        <>
                          <button
                            onClick={cancelEditing}
                            className="rounded px-2 py-0.5 text-xs text-gray-500 transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            {t('common:cancel')}
                          </button>
                          <button
                            onClick={saveEdit}
                            disabled={isSaving}
                            className="rounded bg-orange-600 px-2 py-0.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isSaving ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              t('common:save')
                            )}
                          </button>
                        </>
                      )}
                      <button
                        onClick={closePreview}
                        className="rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  {/* Save error */}
                  {saveError && (
                    <div className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400">
                      <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                      <span className="flex-1">{saveError}</span>
                    </div>
                  )}
                  {/* File metadata row */}
                  {(() => {
                    const fileMeta = skill.files?.find(
                      (f) => f.path === previewFile.path,
                    );
                    if (!fileMeta) {
                      return null;
                    }
                    return (
                      <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                        <span>{formatBytes(fileMeta.sizeBytes)}</span>
                        {fileMeta.contentType && (
                          <span>{fileMeta.contentType}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* File content */}
                <div className="flex-1 overflow-auto">
                  {previewFile.isLoading ? (
                    <div className="space-y-2 p-3">
                      {Array.from({ length: 10 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-3 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
                          style={{ width: `${40 + Math.random() * 50}%` }}
                        />
                      ))}
                    </div>
                  ) : previewFile.error ? (
                    <div className="flex flex-col items-center justify-center p-6 text-center">
                      <AlertTriangle className="mb-2 h-5 w-5 text-red-400" />
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {previewFile.error}
                      </p>
                      <button
                        onClick={() => loadFileContent(previewFile.path)}
                        className="mt-2 rounded-md bg-orange-600 px-3 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-orange-700"
                      >
                        {t('tryAgain')}
                      </button>
                    </div>
                  ) : isEditing ? (
                    <div className="flex h-full">
                      {/* Line numbers gutter */}
                      <div
                        className="flex-shrink-0 border-r border-gray-200 bg-gray-50 px-2 py-2 text-right font-mono text-xs leading-relaxed text-gray-400 select-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-600"
                        aria-hidden="true"
                      >
                        {(editingContent ?? '').split('\n').map((_, i) => (
                          <div key={i}>{i + 1}</div>
                        ))}
                      </div>
                      <textarea
                        className="h-full w-full resize-none border-0 bg-transparent px-3 py-2 font-mono text-xs leading-relaxed focus:ring-0 focus:outline-none dark:text-gray-100"
                        value={editingContent ?? ''}
                        onChange={(e) => setEditingContent(e.target.value)}
                        spellCheck={false}
                      />
                    </div>
                  ) : isTextFile(previewFile.path) ? (
                    <div className="overflow-auto bg-[#1e1e1e]">
                      <CodePane
                        code={previewFile.content}
                        lang={langFromPath(previewFile.path)}
                        maxHeight="none"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center p-8 text-center">
                      <File className="mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('binaryFile')}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                        {t('previewNotAvailableForThisFileType')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-10">
            <Upload className="mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {t('noFilesYet')}
            </p>
            {!readonly && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {t('dragAndDropFilesHereOrClick')}
              </p>
            )}
          </div>
        )}
      </div>
      {/* Upload progress */}
      {uploadProgress.size > 0 && (
        <div className="mt-2 space-y-1">
          {Array.from(uploadProgress.entries()).map(([name, progress]) => (
            <div key={name} className="flex items-center gap-2 text-xs">
              <span className="max-w-[120px] truncate text-gray-500">
                {name}
              </span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full rounded-full bg-orange-500 transition-all duration-300"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              <span className="text-gray-400 tabular-nums">
                {progress.percentage}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
